import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaSignOutAlt, FaMapMarkerAlt, FaPhone, FaEnvelope, FaFilePdf,
  FaCheckDouble, FaArrowLeft, FaArrowRight, FaStore, FaPlus, FaTimes,
} from 'react-icons/fa';
import { toastSuccess, toastError } from '../utils/toast';
import Money from './ui/Money';
import { money } from '../utils/format';
import { getPresetRange, startOfDayMVD, endOfDayMVD } from '../utils/dateRanges';
import Spinner, { LoadingScreen } from './ui/Spinner';
import ConfirmDialog from './ui/ConfirmDialog';
import DateRangePresets from './ui/DateRangePresets';
import logo from '../assets/logo.png';

const RetirosAdminPage = () => {
  const [restaurantes, setRestaurantes] = useState([]);
  const [retiros, setRetiros] = useState([]);
  const [selectedRestaurante, setSelectedRestaurante] = useState(null);
  const [selectedRetiros, setSelectedRetiros] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [filteredAmount, setFilteredAmount] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  // Loading / submitting states
  const [loadingRestaurantes, setLoadingRestaurantes] = useState(true);
  // Cuentas archivadas: las de prueba y las que ya no son clientes ensucian el
  // resumen (un "1 en riesgo" que en realidad es una cuenta vieja de prueba se
  // vuelve ruido que hay que filtrar mentalmente todos los días). Se guarda en
  // el navegador: es una preferencia de quien mira el panel, no un dato del
  // negocio, y así no hace falta una columna nueva.
  const [ocultos, setOcultos] = useState(() => {
    try {
      const guardado = JSON.parse(localStorage.getItem('si_admin_ocultos') || '[]');
      return Array.isArray(guardado) ? guardado : [];
    } catch {
      return [];
    }
  });
  const [verOcultos, setVerOcultos] = useState(false);

  const alternarOculto = (id) => {
    setOcultos((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem('si_admin_ocultos', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [loadingRetiros, setLoadingRetiros] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Preset state for date range
  const [datePreset, setDatePreset] = useState('custom');

  // Confirm dialog
  const [confirmMark, setConfirmMark] = useState(false);

  // Alta de restaurante (solo superadmin)
  const EMPTY_NUEVO = {
    Nombre: '', Direccion: '', email: '', telefono: '',
    adminNombre: '', adminApellido: '', ciAdmin: '', adminEmail: '', adminTelefono: '', adminPassword: '',
  };
  const [showNuevo, setShowNuevo] = useState(false);
  const [nuevo, setNuevo] = useState(EMPTY_NUEVO);
  const [creandoRest, setCreandoRest] = useState(false);

  const filteredRetiros = retiros.filter(r => {
    if (!r.fecha) return false;
    const d = new Date(r.fecha);
    if (startDate && d < startOfDayMVD(startDate)) return false;
    if (endDate && d > endOfDayMVD(endDate)) return false;
    return true;
  });

  const sortedRetiros = [...filteredRetiros].sort(
    (a, b) => new Date(b.fecha) - new Date(a.fecha)
  );

  const totalPages = Math.max(1, Math.ceil(sortedRetiros.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const currentRetiros = sortedRetiros.slice(startIndex, startIndex + PAGE_SIZE);

  const totalCompleted = filteredRetiros
    .filter(r => r.estado.toLowerCase() === 'completado')
    .reduce((sum, r) => sum + r.propina, 0);

  const totalPendiente = filteredRetiros
    .filter(r => r.estado.toLowerCase() === 'pendiente')
    .reduce((sum, r) => sum + r.propina, 0);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const fetchRestaurantes = async () => {
    setLoadingRestaurantes(true);
    try {
      const res = await axios.get(`${apiUrl}/restaurantes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRestaurantes(res.data.restaurantes);
    } catch (err) {
      console.error('Error al obtener restaurantes:', err);
      toastError('No se pudieron cargar los restaurantes.');
    } finally {
      setLoadingRestaurantes(false);
    }
  };

  useEffect(() => {
    fetchRestaurantes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRetiros = async (idRestaurante) => {
    setLoadingRetiros(true);
    try {
      const res = await axios.get(`${apiUrl}/restaurante/${idRestaurante}/retiros`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRetiros(res.data.empleados);
      const restaurante = restaurantes.find((r) => r.Id === idRestaurante);
      setSelectedRestaurante(restaurante);
      setSelectedRetiros([]);
      setFilteredAmount(null);
      setPage(1);
    } catch (err) {
      console.error('Error al obtener los retiros:', err);
      toastError('No se pudieron cargar los retiros del restaurante.');
    } finally {
      setLoadingRetiros(false);
    }
  };

  const handleNuevoChange = (e) => {
    const { name, value } = e.target;
    setNuevo((prev) => ({ ...prev, [name]: value }));
  };

  const crearRestaurante = async (e) => {
    e.preventDefault();
    if (creandoRest) return;

    // Validación mínima en cliente (el backend revalida igual).
    const requeridos = ['Nombre', 'Direccion', 'email', 'telefono', 'adminNombre', 'adminApellido', 'ciAdmin', 'adminEmail', 'adminPassword'];
    const falta = requeridos.find((k) => !String(nuevo[k]).trim());
    if (falta) {
      toastError('Completá todos los campos obligatorios.');
      return;
    }
    if (isNaN(Number(nuevo.ciAdmin))) {
      toastError('La cédula del administrador debe ser numérica.');
      return;
    }
    if (nuevo.adminPassword.length < 6) {
      toastError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setCreandoRest(true);
    try {
      await axios.post(
        `${apiUrl}/restaurante`,
        {
          Nombre: nuevo.Nombre,
          Direccion: nuevo.Direccion,
          email: nuevo.email,
          telefono: nuevo.telefono.trim(),
          adminNombre: nuevo.adminNombre,
          adminApellido: nuevo.adminApellido,
          ciAdmin: Number(nuevo.ciAdmin),
          adminEmail: nuevo.adminEmail,
          adminTelefono: nuevo.adminTelefono,
          adminPassword: nuevo.adminPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Restaurante creado correctamente.');
      setNuevo(EMPTY_NUEVO);
      setShowNuevo(false);
      await fetchRestaurantes();
    } catch (err) {
      console.error('Error al crear restaurante:', err);
      toastError(err.response?.data?.error || 'No se pudo crear el restaurante.');
    } finally {
      setCreandoRest(false);
    }
  };

  const handleCheckboxChange = (retiroId) => {
    setSelectedRetiros((prev) =>
      prev.includes(retiroId)
        ? prev.filter((id) => id !== retiroId)
        : [...prev, retiroId]
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  const marcarComoCompletados = async () => {
    if (isMarking) return;
    setIsMarking(true);
    try {
      const { data } = await axios.patch(
        `${apiUrl}/retiros/marcarCompletados`,
        { ids: selectedRetiros },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const n = data?.actualizados ?? selectedRetiros.length;
      if (n === 0) {
        toastError('No se marcó ningún retiro. Los pagos de cierres se confirman desde el panel del restaurante.');
      } else {
        toastSuccess(`${n} retiro(s) marcados como completados.`);
      }
      setConfirmMark(false);
      await fetchRetiros(selectedRestaurante.Id);
      await fetchRestaurantes();
    } catch (err) {
      console.error('Error al marcar como completados:', err);
      toastError('No se pudieron marcar como completados.');
    } finally {
      setIsMarking(false);
    }
  };

  // Date range presets
  const applyDatePreset = (key) => {
    setDatePreset(key);
    if (key === 'custom') return;
    const range = getPresetRange(key);
    if (range) {
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedRestaurante]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const downloadPaymentReport = async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start', startDate);
      if (endDate) params.append('end', endDate);
      else params.append('period', 'mensual');
      params.append('format', 'pdf');

      const res = await axios.get(
        `${apiUrl}/restaurante/${selectedRestaurante.Id}/reporte-pagos?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_pagos_${selectedRestaurante.Id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toastSuccess('Reporte descargado correctamente.');
    } catch (err) {
      console.error('Error al descargar PDF:', err);
      toastError('No se pudo descargar el reporte PDF.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-bg text-ink">
      {/* Header sticky */}
      <header className="bg-surface border-b border-line sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="ServiceIn"
              className="w-9 h-9 rounded-lg object-contain"
            />
            <div>
              <div className="si-h3 leading-tight">ServiceIn</div>
              <div className="text-[10px] uppercase tracking-wider text-ink-3">Admin</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="si-btn-danger"
          >
            <FaSignOutAlt className="mr-2" />
            Cerrar sesión
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alta de restaurante (solo superadmin) */}
        <div className="si-card p-5 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="si-h2">Nuevo restaurante</h3>
              <p className="text-sm text-ink-3 mt-0.5">Dá de alta un restaurante y su usuario administrador.</p>
            </div>
            <button
              onClick={() => setShowNuevo((v) => !v)}
              className={showNuevo
                ? 'px-3 py-1.5 rounded-lg border border-line bg-surface text-ink-2 hover:bg-surface-2 transition flex items-center'
                : 'si-btn-primary'}
            >
              {showNuevo
                ? (<><FaTimes className="mr-2" />Cancelar</>)
                : (<><FaPlus className="mr-2" />Crear restaurante</>)}
            </button>
          </div>

          {showNuevo && (
            <form onSubmit={crearRestaurante} className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <h4 className="text-xs uppercase tracking-wider text-ink-3 font-semibold">Datos del restaurante</h4>
              </div>
              <div>
                <label className="si-label">Nombre *</label>
                <input name="Nombre" value={nuevo.Nombre} onChange={handleNuevoChange} className="si-input" maxLength={75} />
              </div>
              <div>
                <label className="si-label">Dirección *</label>
                <input name="Direccion" value={nuevo.Direccion} onChange={handleNuevoChange} className="si-input" maxLength={75} />
              </div>
              <div>
                <label className="si-label">Email *</label>
                <input name="email" type="email" value={nuevo.email} onChange={handleNuevoChange} className="si-input" maxLength={75} />
              </div>
              <div>
                <label className="si-label">Teléfono *</label>
                <input name="telefono" type="tel" value={nuevo.telefono} onChange={handleNuevoChange} className="si-input" maxLength={20} />
              </div>

              <div className="md:col-span-2 mt-2">
                <h4 className="text-xs uppercase tracking-wider text-ink-3 font-semibold">Usuario administrador (login del restaurante)</h4>
              </div>
              <div>
                <label className="si-label">Nombre *</label>
                <input name="adminNombre" value={nuevo.adminNombre} onChange={handleNuevoChange} className="si-input" maxLength={45} />
              </div>
              <div>
                <label className="si-label">Apellido *</label>
                <input name="adminApellido" value={nuevo.adminApellido} onChange={handleNuevoChange} className="si-input" maxLength={45} />
              </div>
              <div>
                <label className="si-label">Cédula *</label>
                <input name="ciAdmin" type="number" value={nuevo.ciAdmin} onChange={handleNuevoChange} className="si-input" />
              </div>
              <div>
                <label className="si-label">Teléfono</label>
                <input name="adminTelefono" value={nuevo.adminTelefono} onChange={handleNuevoChange} className="si-input" maxLength={20} />
              </div>
              <div>
                <label className="si-label">Email *</label>
                <input name="adminEmail" type="email" value={nuevo.adminEmail} onChange={handleNuevoChange} className="si-input" maxLength={100} />
              </div>
              <div>
                <label className="si-label">Contraseña *</label>
                <input name="adminPassword" type="password" value={nuevo.adminPassword} onChange={handleNuevoChange} className="si-input" />
              </div>

              <div className="md:col-span-2 flex justify-end mt-2">
                <button type="submit" disabled={creandoRest} className="si-btn-primary">
                  {creandoRest
                    ? (<><Spinner size={14} className="mr-2" />Creando...</>)
                    : (<><FaPlus className="mr-2" />Crear restaurante</>)}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="mb-6">
          <h2 className="si-h1">Restaurantes</h2>
          <p className="si-body mt-1">Seleccioná un restaurante para gestionar sus retiros pendientes.</p>
        </div>

        {/* Resumen de lo que hay cargado, sobre las cuentas no archivadas. */}
        {!loadingRestaurantes && (() => {
          const visibles = restaurantes.filter((r) => !ocultos.includes(r.Id));
          const totales = visibles.reduce(
            (acc, r) => ({
              empleados: acc.empleados + num(r.empleadosActivos),
              monto30: acc.monto30 + num(r.monto30),
              pendiente: acc.pendiente + num(r.totalPendiente),
            }),
            { empleados: 0, monto30: 0, pendiente: 0 }
          );
          // El backend viejo no manda estos campos: mejor no mostrar un cero
          // que parece un dato real.
          const hayMetricas = visibles.some((r) => r.empleadosActivos !== undefined);

          return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <div className="si-card p-4">
                <div className="si-eyebrow">Restaurantes</div>
                <div className="text-2xl font-bold si-money mt-1 text-ink">{visibles.length}</div>
              </div>
              <div className="si-card p-4">
                <div className="si-eyebrow">Empleados</div>
                <div className="text-2xl font-bold si-money mt-1 text-ink">
                  {hayMetricas ? totales.empleados : '—'}
                </div>
              </div>
              <div className="si-card p-4">
                <div className="si-eyebrow">Propinas (30 días)</div>
                <div className="text-2xl font-bold mt-1" style={{ color: '#c8f06a' }}>
                  {hayMetricas ? <Money value={totales.monto30} /> : '—'}
                </div>
              </div>
              <div className="si-card p-4">
                <div className="si-eyebrow">Pendiente de pago</div>
                <div
                  className="text-2xl font-bold mt-1"
                  style={{ color: totales.pendiente > 0 ? '#c8f06a' : '#8a8a94' }}
                >
                  <Money value={totales.pendiente} />
                </div>
              </div>
            </div>
          );
        })()}

        {loadingRestaurantes ? (
          <div className="si-card p-6">
            <LoadingScreen label="Cargando restaurantes..." />
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {restaurantes
              /* Antes había un `filter(r => r.Id !== 8)` hardcodeado para
                 esconder una cuenta. Cuando el auto-increment volvió a pasar
                 por el 8, ese filtro empezó a esconder un cliente real. Ahora
                 se archiva por elección y queda guardado en el navegador. */
              .filter((r) => verOcultos || !ocultos.includes(r.Id))
              .map((r) => {
                const isSelected = selectedRestaurante?.Id === r.Id;
                const archivado = ocultos.includes(r.Id);

                return (
                  <div
                    key={r.Id}
                    className={`si-card p-5 transition-all hover:border-purple/40 ${
                      isSelected ? 'border-purple/60 ring-2 ring-purple/20' : ''
                    } ${archivado ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <button
                        onClick={() => fetchRetiros(r.Id)}
                        className="flex items-center gap-2.5 text-left min-w-0 flex-1"
                      >
                        <div
                          className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0"
                          style={{ background: 'rgba(178, 140, 245, 0.15)', color: '#b28cf5' }}
                        >
                          <FaStore />
                        </div>
                        <h3 className="si-h3 truncate">{r.Nombre}</h3>
                      </button>
                      <button
                        onClick={() => alternarOculto(r.Id)}
                        className="text-[10px] text-ink-3 hover:text-ink transition flex-shrink-0"
                        title={archivado
                          ? 'Volver a mostrarla en la lista y en el resumen'
                          : 'Sacarla de la lista y del resumen. No se borra nada: se puede restaurar cuando quieras.'}
                      >
                        {archivado ? 'Restaurar' : 'Archivar'}
                      </button>
                    </div>

                    <button
                      onClick={() => fetchRetiros(r.Id)}
                      className="w-full text-left grid grid-cols-2 gap-x-3 gap-y-2.5 py-3 border-y border-line"
                    >
                      <Metrica
                        label="Equipo"
                        valor={texto(r.empleadosActivos)}
                        extra={num(r.empleadosInactivos) > 0 ? `+${num(r.empleadosInactivos)} de baja` : null}
                      />
                      <Metrica label="Último cierre" valor={fechaCorta(r.ultimoCierre)} />
                      <Metrica label="Cierres (30d)" valor={texto(r.cierres30)} />
                      <Metrica
                        label="Propinas (30d)"
                        valor={r.monto30 === undefined ? '—' : <Money value={num(r.monto30)} />}
                        destacado={num(r.monto30) > 0}
                      />
                      <Metrica
                        label="Sin retirar"
                        valor={r.saldoAcumulado === undefined ? '—' : <Money value={num(r.saldoAcumulado)} />}
                        extra={num(r.saldoDeBajas) > 0 ? `+ ${money(num(r.saldoDeBajas))} de bajas` : null}
                      />
                      <Metrica label="Reparto" valor={r.modoReparto === 'puntos' ? 'Puntos' : 'Porcentaje'} />
                    </button>

                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className="text-[11px] text-ink-3">ID {r.Id}</span>
                      {num(r.totalPendiente) > 0 ? (
                        <span className="text-xs">
                          <span className="text-ink-3">Pendiente de pago </span>
                          <span className="font-bold" style={{ color: '#c8f06a' }}>
                            <Money value={num(r.totalPendiente)} />
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-ink-3">Sin pagos pendientes</span>
                      )}
                    </div>

                    <details className="mt-3">
                      <summary className="text-[11px] text-ink-3 hover:text-ink cursor-pointer select-none">
                        Contacto
                      </summary>
                      <div className="text-sm space-y-1.5 text-ink-2 mt-2">
                        <p className="flex items-center gap-2"><FaMapMarkerAlt className="text-ink-3 text-xs flex-shrink-0" /><span className="truncate">{r.Direccion}</span></p>
                        <p className="flex items-center gap-2"><FaPhone className="text-ink-3 text-xs flex-shrink-0" /><span className="truncate">{r.telefono}</span></p>
                        <p className="flex items-center gap-2"><FaEnvelope className="text-ink-3 text-xs flex-shrink-0" /><span className="truncate">{r.email}</span></p>
                      </div>
                    </details>
                  </div>
                );
              })}
          </div>

          {ocultos.length > 0 && (
            <button
              onClick={() => setVerOcultos((v) => !v)}
              className="mt-4 text-xs text-ink-3 hover:text-ink transition"
            >
              {verOcultos
                ? 'Esconder las archivadas'
                : `Ver ${ocultos.length} archivada${ocultos.length === 1 ? '' : 's'}`}
            </button>
          )}
          </>
        )}

        {selectedRestaurante && (
          <div className="mt-10">
            <div className="flex items-baseline gap-2 mb-4">
              <h2 className="text-xl font-bold text-ink">Retiros de</h2>
              <span className="text-xl font-bold" style={{ color: '#b28cf5' }}>
                {selectedRestaurante.Nombre}
              </span>
            </div>

            {/* Filtros y totales */}
            <div className="si-card p-5 mb-5">
              <div className="mb-4">
                <DateRangePresets active={datePreset} onChange={applyDatePreset} />
              </div>
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <label className="si-label">Desde</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => { setStartDate(e.target.value); setDatePreset('custom'); }}
                    className="si-input"
                  />
                </div>
                <div>
                  <label className="si-label">Hasta</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => { setEndDate(e.target.value); setDatePreset('custom'); }}
                    className="si-input"
                  />
                </div>
                <div className="ml-auto flex gap-4">
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wider text-ink-3 mb-1">Pendiente</div>
                    <div className="text-xl font-bold text-warn"><Money value={totalPendiente} /></div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wider text-ink-3 mb-1">Completado</div>
                    <div className="text-xl font-bold" style={{ color: '#c8f06a' }}><Money value={totalCompleted} /></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabla de retiros */}
            {loadingRetiros ? (
              <div className="si-card p-6">
                <LoadingScreen label="Cargando retiros..." />
              </div>
            ) : (
            <div className="si-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-line bg-surface-2/40">
                      <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Empleado</th>
                      <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Propina</th>
                      <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Fecha</th>
                      <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Estado</th>
                      <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider text-ink-3">Sel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRetiros.map((r, i) => {
                      const d = new Date(r.fecha);
                      const dateStr = d.toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        timeZone: 'America/Montevideo'
                      });
                      const timeStr = d.toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'America/Montevideo'
                      });

                      return (
                        <tr
                          key={r.retiroId}
                          className={`${i !== currentRetiros.length - 1 ? 'border-b border-line' : ''} hover:bg-surface-2/40 transition-colors`}
                        >
                          <td className="px-5 py-3 text-ink">{r.Nombre} {r.Apellido}</td>
                          <td className="px-5 py-3 font-semibold" style={{ color: '#c8f06a' }}>
                            ${r.propina}
                          </td>
                          <td className="px-5 py-3 text-ink-2">
                            <div>{dateStr}</div>
                            <div className="text-xs text-ink-3">{timeStr}</div>
                          </td>
                          <td className="px-5 py-3">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                                r.estado === 'pendiente'
                                  ? 'bg-warn/15 text-warn border border-warn/30'
                                  : 'bg-lime/15 text-lime border border-lime/30'
                              }`}
                            >
                              {r.estado}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            {r.estado === 'pendiente' && (
                              <input
                                type="checkbox"
                                checked={selectedRetiros.includes(r.retiroId)}
                                onChange={() => handleCheckboxChange(r.retiroId)}
                                className="form-checkbox h-4 w-4 rounded border-line bg-surface focus:ring-purple"
                                style={{ accentColor: '#b28cf5' }}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {currentRetiros.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-5 py-8 text-center text-ink-3">
                          No hay retiros en este filtro. Probá con otro rango o restaurante.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              <div className="border-t border-line px-5 py-3 flex items-center justify-between flex-wrap gap-3 text-sm">
                <span className="text-ink-3">
                  Mostrando {sortedRetiros.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + PAGE_SIZE, sortedRetiros.length)} de {sortedRetiros.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border border-line bg-surface text-ink-2 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
                  >
                    <FaArrowLeft className="text-[10px]" />
                    Anterior
                  </button>
                  <span className="text-ink-2 px-2">Página {currentPage} de {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages || sortedRetiros.length === 0}
                    className="px-3 py-1.5 rounded-lg border border-line bg-surface text-ink-2 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
                  >
                    Siguiente
                    <FaArrowRight className="text-[10px]" />
                  </button>
                </div>
              </div>
            </div>
            )}

            {selectedRetiros.length > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setConfirmMark(true)}
                  disabled={isMarking}
                  className="si-btn-success"
                >
                  {isMarking ? (
                    <>
                      <Spinner size={14} className="mr-2" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FaCheckDouble className="mr-2" />
                      Marcar como completados ({selectedRetiros.length})
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {selectedRestaurante && (
          <div className="mt-8 si-card p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="si-h2">Reporte de pagos</h3>
                <p className="text-sm text-ink-3 mt-0.5">
                  Descargá un PDF con el resumen de pagos efectuados al restaurante.
                </p>
              </div>
              <button
                onClick={downloadPaymentReport}
                disabled={isDownloadingPdf}
                className="si-btn-primary"
              >
                {isDownloadingPdf ? (
                  <>
                    <Spinner size={14} className="mr-2" />
                    Generando...
                  </>
                ) : (
                  <>
                    <FaFilePdf className="mr-2" />
                    Descargar PDF
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmMark}
        onClose={() => setConfirmMark(false)}
        onConfirm={marcarComoCompletados}
        title={`¿Marcar ${selectedRetiros.length} retiro(s) como completados?`}
        message="Esta acción confirma que los pagos seleccionados ya fueron efectuados. No podrás revertirla desde esta interfaz."
        confirmLabel="Sí, marcar como completados"
        cancelLabel="Cancelar"
        variant="primary"
        loading={isMarking}
      />
    </div>
  );
};

/**
 * Castea lo que manda la API. Los SUM() y COUNT() de MySQL llegan como STRING
 * por mysql2, y sumarlos sin castear concatena en vez de sumar: fue lo que hizo
 * que el total de pendientes mostrara 60.560.000 en vez de 6.056.
 */
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Un valor numérico para mostrar, o un guión si el dato no vino. */
const texto = (v) => (v === undefined || v === null ? '—' : String(num(v)));

/** Fecha corta, o "nunca" si el restaurante todavía no cerró ningún día. */
const fechaCorta = (iso) => {
  if (iso === undefined) return '—';
  if (!iso) return 'Nunca';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-UY', { timeZone: 'America/Montevideo', day: '2-digit', month: '2-digit', year: '2-digit' });
};

/** Una métrica de la tarjeta: rótulo chico arriba, valor abajo. */
function Metrica({ label, valor, extra, destacado, alerta }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-ink-3">{label}</div>
      <div
        className={`text-sm font-semibold truncate ${destacado ? '' : 'text-ink'}`}
        style={alerta ? { color: '#f59e0b' } : destacado ? { color: '#c8f06a' } : undefined}
      >
        {valor}
      </div>
      {extra && (
        <div className="text-[10px] text-ink-3 truncate" style={alerta ? { color: '#f59e0b' } : undefined}>
          {extra}
        </div>
      )}
    </div>
  );
}

export default RetirosAdminPage;
